// 🔹 Define the type here so it’s reusable across components
export type NavLink = {
  id: number;
  /** Visible label when `i18nKey` is not set (deep trees, mega-menu leaves). */
  label?: string;
  /** Key under message namespace `nav` (e.g. `root.services`). */
  i18nKey?: string;
  href?: string;
  external?: boolean;
  disabled?: boolean;
  children?: NavLink[];
};

export type NavLinkChild = {
  id: number;
  label?: string;
  i18nKey?: string;
  href?: string;
  children?: NavLinkChild[];
};

// 🔹 Export your links using the type
export const links: NavLink[] = [
  {
    id: 1,
    i18nKey: "root.services",
    href: "/services",
    children: [
      {
        id: 1,
        label: "Full-cycle services",
        href: "/services/full-cycle",
        children: [
          {
            id: 1,
            label: "IT consulting & advisory",
            href: "/services/full-cycle/it-consulting",
          },
          {
            id: 2,
            label: "Web development",
            href: "/services/full-cycle/web-development",
          },
          {
            id: 3,
            label: "Mobile development",
            href: "/services/full-cycle/mobile-development",
          },
          {
            id: 4,
            label: "Embedded software development",
            href: "/services/full-cycle/embedded-software",
          },
          {
            id: 5,
            label: "Custom software development",
            href: "/services/full-cycle/custom-software",
          },
          {
            id: 6,
            label: "MVP development",
            href: "/services/full-cycle/mvp-development",
          },
          {
            id: 7,
            label: "Cloud development",
            href: "/services/full-cycle/cloud-development",
          },
          {
            id: 8,
            label: "ERP development",
            href: "/services/full-cycle/erp-development",
          },
          {
            id: 9,
            label: "Mainframe support",
            href: "/services/full-cycle/mainframe-support",
          },
          {
            id: 10,
            label: "Legacy modernization",
            href: "/services/full-cycle/legacy-modernization",
          },
          {
            id: 11,
            label: "UI/UX design",
            href: "/services/full-cycle/ui-ux-design",
          },
          {
            id: 12,
            label: "3D design",
            href: "/services/full-cycle/3d-design",
          },
          {
            id: 13,
            label: "Software testing and QA",
            href: "/services/full-cycle/software-testing",
          },
          {
            id: 14,
            label: "Security testing",
            href: "/services/full-cycle/security-testing",
          },
          {
            id: 15,
            label: "Database administration",
            href: "/services/full-cycle/database-administration",
          },
          { id: 16, label: "DevOps", href: "/services/full-cycle/devops" },
          {
            id: 17,
            label: "DevSecOps",
            href: "/services/full-cycle/devsecops",
          },
          { id: 18, label: "Network", href: "/services/full-cycle/network" },
          {
            id: 19,
            label: "IT staff augmentation",
            href: "/services/full-cycle/staff-augmentation",
          },
          {
            id: 20,
            label: "Dedicated team",
            href: "/services/full-cycle/dedicated-team",
          },
        ],
      },
      {
        id: 2,
        label: "Advanced technologies",
        href: "/services/advanced-tech",
        children: [
          {
            id: 1,
            label: "Big data",
            href: "/services/advanced-tech/big-data",
          },
          {
            id: 2,
            label: "Data management",
            href: "/services/advanced-tech/data-management",
          },
          {
            id: 3,
            label: "Data analytics",
            href: "/services/advanced-tech/data-analytics",
          },
          {
            id: 4,
            label: "Data engineering",
            href: "/services/advanced-tech/data-engineering",
          },
          {
            id: 5,
            label: "Data visualization",
            href: "/services/advanced-tech/data-visualization",
          },
          {
            id: 6,
            label: "Business intelligence",
            href: "/services/advanced-tech/business-intelligence",
          },
          {
            id: 7,
            label: "Machine learning",
            href: "/services/advanced-tech/machine-learning",
          },
          {
            id: 8,
            label: "Artificial intelligence",
            href: "/services/advanced-tech/artificial-intelligence",
          },
          {
            id: 9,
            label: "Data science",
            href: "/services/advanced-tech/data-science",
          },
          {
            id: 10,
            label: "Sustainability & ESG",
            href: "/services/advanced-tech/sustainability-esg",
          },
          {
            id: 11,
            label: "Digital transformation",
            href: "/services/advanced-tech/digital-transformation",
          },
          {
            id: 12,
            label: "Business process automation",
            href: "/services/advanced-tech/bpa",
          },
          {
            id: 13,
            label: "Robotic process automation",
            href: "/services/advanced-tech/rpa",
          },
          {
            id: 14,
            label: "Cybersecurity",
            href: "/services/advanced-tech/cybersecurity",
          },
          {
            id: 15,
            label: "Internet of things",
            href: "/services/advanced-tech/iot",
          },
          {
            id: 16,
            label: "Blockchain",
            href: "/services/advanced-tech/blockchain",
          },
          { id: 17, label: "NFT", href: "/services/advanced-tech/nft" },
          {
            id: 18,
            label: "Metaverse",
            href: "/services/advanced-tech/metaverse",
          },
          { id: 19, label: "AR & VR", href: "/services/advanced-tech/ar-vr" },
          {
            id: 20,
            label: "Generative AI engineering",
            href: "/services/advanced-tech/generative-ai",
          },
          {
            id: 21,
            label: "LLM, RAG & knowledge assistants",
            href: "/services/advanced-tech/llm-rag",
          },
          {
            id: 22,
            label: "AI agents & workflow automation",
            href: "/services/advanced-tech/ai-agents",
          },
          {
            id: 23,
            label: "API & systems integration",
            href: "/services/advanced-tech/api-integration",
          },
        ],
      },
    ],
  },
  {
    id: 2,
    i18nKey: "root.technologies",
    href: "/technologies",
    children: [
      {
        id: 1,
        label: "Frontend Developers",
        href: "/technologies/frontend",
        children: [
          { id: 1, label: "React", href: "/technologies/frontend/react" },
          { id: 2, label: "Angular", href: "/technologies/frontend/angular" },
          { id: 3, label: "Vue.js", href: "/technologies/frontend/vue" },
          {
            id: 4,
            label: "JavaScript",
            href: "/technologies/frontend/javascript",
          },
          { id: 5, label: "Svelte", href: "/technologies/frontend/svelte" },
          { id: 6, label: "Next.js", href: "/technologies/frontend/nextjs" },
          { id: 7, label: "TypeScript", href: "/technologies/frontend/typescript" },
          { id: 8, label: "Remix", href: "/technologies/frontend/remix" },
        ],
      },
      {
        id: 2,
        label: "Backend Developers",
        href: "/technologies/backend",
        children: [
          { id: 1, label: "Java", href: "/technologies/backend/java" },
          { id: 2, label: ".NET", href: "/technologies/backend/dotnet" },
          { id: 3, label: "Python", href: "/technologies/backend/python" },
          { id: 4, label: "PHP", href: "/technologies/backend/php" },
          { id: 5, label: "Rust", href: "/technologies/backend/rust" },
          { id: 6, label: "C/C++", href: "/technologies/backend/cpp" },
          { id: 7, label: "Node.js", href: "/technologies/backend/nodejs" },
          { id: 8, label: "Unity", href: "/technologies/backend/unity" },
          { id: 9, label: "Ruby on Rails", href: "/technologies/backend/ruby" },
          { id: 10, label: "Golang", href: "/technologies/backend/golang" },
          {
            id: 11,
            label: "Unreal Engine",
            href: "/technologies/backend/unreal",
          },
          { id: 12, label: "Cobol", href: "/technologies/backend/cobol" },
          {
            id: 13,
            label: "Full stack",
            href: "/technologies/backend/fullstack",
          },
          {
            id: 14,
            label: "MEAN stack",
            href: "/technologies/backend/mean-stack",
          },
          { id: 15, label: "NestJS", href: "/technologies/backend/nestjs" },
          { id: 16, label: "Spring Boot", href: "/technologies/backend/spring" },
          { id: 17, label: "FastAPI", href: "/technologies/backend/fastapi" },
        ],
      },
      {
        id: 3,
        label: "Mobile App Developers",
        href: "/technologies/mobile",
        children: [
          { id: 1, label: "iOS", href: "/technologies/mobile/ios" },
          { id: 2, label: "Android", href: "/technologies/mobile/android" },
          { id: 3, label: "Flutter", href: "/technologies/mobile/flutter" },
          {
            id: 4,
            label: "React Native",
            href: "/technologies/mobile/react-native",
          },
          { id: 5, label: "Xamarin", href: "/technologies/mobile/xamarin" },
          {
            id: 6,
            label: ".NET MAUI",
            href: "/technologies/mobile/dotnet-maui",
          },
          { id: 7, label: "Swift", href: "/technologies/mobile/swift" },
          { id: 8, label: "Kotlin", href: "/technologies/mobile/kotlin" },
          { id: 9, label: "Mendix", href: "/technologies/mobile/mendix" },
        ],
      },
      {
        id: 4,
        label: "Cloud",
        href: "/technologies/cloud",
        children: [
          { id: 1, label: "AWS", href: "/technologies/cloud/aws" },
          { id: 2, label: "Azure", href: "/technologies/cloud/azure" },
          {
            id: 3,
            label: "Google Cloud (GCP)",
            href: "/technologies/cloud/gcp",
          },
          {
            id: 4,
            label: "SharePoint",
            href: "/technologies/cloud/sharepoint",
          },
        ],
      },
      {
        id: 5,
        label: "ERP & CRM",
        href: "/technologies/erp-crm",
        children: [
          { id: 1, label: "SAP", href: "/technologies/erp-crm/sap" },
          {
            id: 2,
            label: "MS Dynamics 365",
            href: "/technologies/erp-crm/ms-dynamics",
          },
          { id: 3, label: "Odoo", href: "/technologies/erp-crm/odoo" },
          {
            id: 4,
            label: "Salesforce",
            href: "/technologies/erp-crm/salesforce",
          },
        ],
      },
      {
        id: 6,
        label: "Data, AI & ML",
        href: "/technologies/data-ai-ml",
        children: [
          { id: 1, label: "Python (data & AI)", href: "/technologies/data-ai-ml/python" },
          { id: 2, label: "TensorFlow", href: "/technologies/data-ai-ml/tensorflow" },
          { id: 3, label: "PyTorch", href: "/technologies/data-ai-ml/pytorch" },
          { id: 4, label: "scikit-learn", href: "/technologies/data-ai-ml/sklearn" },
          { id: 5, label: "LangChain / LangGraph", href: "/technologies/data-ai-ml/langchain" },
          { id: 6, label: "Hugging Face", href: "/technologies/data-ai-ml/huggingface" },
          { id: 7, label: "OpenAI / Anthropic APIs", href: "/technologies/data-ai-ml/llm-apis" },
          { id: 8, label: "Snowflake", href: "/technologies/data-ai-ml/snowflake" },
          { id: 9, label: "Databricks", href: "/technologies/data-ai-ml/databricks" },
          { id: 10, label: "Kafka", href: "/technologies/data-ai-ml/kafka" },
        ],
      },
      {
        id: 7,
        label: "DevOps, data stores & platform",
        href: "/technologies/devops-data",
        children: [
          { id: 1, label: "Kubernetes", href: "/technologies/devops-data/kubernetes" },
          { id: 2, label: "Docker", href: "/technologies/devops-data/docker" },
          { id: 3, label: "Terraform", href: "/technologies/devops-data/terraform" },
          { id: 4, label: "PostgreSQL", href: "/technologies/devops-data/postgresql" },
          { id: 5, label: "MongoDB", href: "/technologies/devops-data/mongodb" },
          { id: 6, label: "Redis", href: "/technologies/devops-data/redis" },
          { id: 7, label: "Elasticsearch", href: "/technologies/devops-data/elasticsearch" },
          { id: 8, label: "CI/CD (GitHub Actions, GitLab)", href: "/technologies/devops-data/cicd" },
          { id: 9, label: "Argo CD / GitOps", href: "/technologies/devops-data/gitops" },
          { id: 10, label: "Observability (Prometheus, Grafana)", href: "/technologies/devops-data/observability" },
        ],
      },
    ],
  },
  {
    id: 3,
    i18nKey: "root.aiAgents",
    href: "/ai-agents",
    children: [
      {
        id: 1,
        label: "Solutions & delivery",
        href: "/ai-agents/solutions",
        children: [
          { id: 1, label: "AI strategy & discovery", href: "/ai-agents/strategy" },
          { id: 2, label: "Generative AI & copilots", href: "/ai-agents/generative-ai" },
          { id: 3, label: "LLM apps, RAG & knowledge bases", href: "/ai-agents/llm-rag" },
          { id: 4, label: "AI agents & autonomous workflows", href: "/ai-agents/agents" },
          { id: 5, label: "Conversational AI & voice", href: "/ai-agents/conversational-ai" },
          { id: 6, label: "Computer vision", href: "/ai-agents/computer-vision" },
          { id: 7, label: "Document & process intelligence", href: "/ai-agents/document-ai" },
          { id: 8, label: "ML engineering & MLOps", href: "/ai-agents/mlops" },
          { id: 9, label: "Data foundations for AI", href: "/ai-agents/data-for-ai" },
          { id: 10, label: "AI integration & enterprise APIs", href: "/ai-agents/integration" },
        ],
      },
      {
        id: 2,
        label: "Trust, governance & security",
        href: "/ai-agents/trust",
        children: [
          { id: 1, label: "Responsible AI & ethics", href: "/ai-agents/responsible-ai" },
          { id: 2, label: "AI security & red teaming", href: "/ai-agents/ai-security" },
          { id: 3, label: "Model evaluation & monitoring", href: "/ai-agents/model-observability" },
          { id: 4, label: "Privacy-preserving AI", href: "/ai-agents/privacy-ai" },
          { id: 5, label: "Regulatory readiness (EU AI Act, GDPR)", href: "/ai-agents/compliance" },
          { id: 6, label: "Human-in-the-loop operations", href: "/ai-agents/human-in-the-loop" },
        ],
      },
    ],
  },
  {
    id: 4,
    i18nKey: "root.industries",
    href: "/industries",
    children: [
      {
        id: 1,
        label: "Banking, financial services & insurance",
        href: "/industries/bfsi",
        children: [
          { id: 1, label: "Banking", href: "/industries/banking" },
          { id: 2, label: "Financial services", href: "/industries/finance" },
          { id: 3, label: "Insurance", href: "/industries/insurance" },
          { id: 4, label: "FinTech & payments", href: "/industries/fintech" },
          { id: 5, label: "Capital markets & asset management", href: "/industries/capital-markets" },
        ],
      },
      {
        id: 2,
        label: "Healthcare, pharma & life sciences",
        href: "/industries/health-life-sciences",
        children: [
          { id: 1, label: "Healthcare providers", href: "/industries/healthcare" },
          { id: 2, label: "Pharmaceuticals", href: "/industries/pharma" },
          { id: 3, label: "Medtech & devices", href: "/industries/medtech" },
          { id: 4, label: "Biotech & life sciences R&D", href: "/industries/biotech" },
        ],
      },
      {
        id: 3,
        label: "Education, research & nonprofit",
        href: "/industries/education-research",
        children: [
          { id: 1, label: "Education & EdTech", href: "/industries/education" },
          { id: 2, label: "Research & higher education", href: "/industries/research" },
          { id: 3, label: "Nonprofit & NGOs", href: "/industries/nonprofit" },
        ],
      },
      {
        id: 4,
        label: "Technology, telecom & enterprise software",
        href: "/industries/technology-telecom",
        children: [
          { id: 1, label: "Telecommunications", href: "/industries/telecommunications" },
          { id: 2, label: "Enterprise & B2B software", href: "/industries/enterprise" },
          { id: 3, label: "SaaS & cloud platforms", href: "/industries/saas" },
          { id: 4, label: "Cybersecurity industry", href: "/industries/cybersecurity" },
        ],
      },
      {
        id: 5,
        label: "Retail, e-commerce & consumer",
        href: "/industries/retail-consumer",
        children: [
          { id: 1, label: "Retail", href: "/industries/retail" },
          { id: 2, label: "E-commerce & marketplaces", href: "/industries/ecommerce" },
          { id: 3, label: "Consumer brands & CPG", href: "/industries/cpg" },
          { id: 4, label: "Luxury & fashion", href: "/industries/luxury-fashion" },
        ],
      },
      {
        id: 6,
        label: "Media, entertainment & gaming",
        href: "/industries/media-gaming",
        children: [
          { id: 1, label: "Media & entertainment", href: "/industries/media" },
          { id: 2, label: "Gaming & esports", href: "/industries/gaming" },
          { id: 3, label: "Sports & fitness", href: "/industries/sports" },
        ],
      },
      {
        id: 7,
        label: "Manufacturing, automotive & aerospace",
        href: "/industries/manufacturing-mobility",
        children: [
          { id: 1, label: "Manufacturing & Industry 4.0", href: "/industries/manufacturing" },
          { id: 2, label: "Automotive & mobility", href: "/industries/automotive" },
          { id: 3, label: "Aerospace & defense", href: "/industries/aerospace-defense" },
          { id: 4, label: "Industrial automation & robotics", href: "/industries/industrial-automation" },
        ],
      },
      {
        id: 8,
        label: "Logistics, supply chain & transportation",
        href: "/industries/logistics-supply-chain",
        children: [
          { id: 1, label: "Logistics & 3PL", href: "/industries/logistics" },
          { id: 2, label: "Shipping & maritime", href: "/industries/maritime" },
          { id: 3, label: "Airlines & aviation", href: "/industries/aviation" },
        ],
      },
      {
        id: 9,
        label: "Marketing, legal & professional services",
        href: "/industries/professional-services",
        children: [
          { id: 1, label: "Marketing & advertising", href: "/industries/marketing" },
          { id: 2, label: "Legal & law firms", href: "/industries/legal" },
          { id: 3, label: "Consulting & advisory", href: "/industries/consulting" },
        ],
      },
      {
        id: 10,
        label: "Government & public sector",
        href: "/industries/government-public",
        children: [
          { id: 1, label: "Government & agencies", href: "/industries/government" },
          { id: 2, label: "Defense & national security", href: "/industries/defense" },
          { id: 3, label: "Smart cities & civic tech", href: "/industries/smart-cities" },
        ],
      },
      {
        id: 11,
        label: "Energy, utilities & natural resources",
        href: "/industries/energy-resources",
        children: [
          { id: 1, label: "Energy & utilities", href: "/industries/energy" },
          { id: 2, label: "Oil & gas", href: "/industries/oil-gas" },
          { id: 3, label: "Renewables & cleantech", href: "/industries/renewables" },
          { id: 4, label: "Mining & metals", href: "/industries/mining" },
        ],
      },
      {
        id: 12,
        label: "Construction, infrastructure & real estate",
        href: "/industries/built-environment",
        children: [
          { id: 1, label: "Construction & engineering", href: "/industries/construction" },
          { id: 2, label: "Real estate & PropTech", href: "/industries/real-estate" },
          { id: 3, label: "PropTech platforms", href: "/industries/proptech" },
        ],
      },
      {
        id: 13,
        label: "Agriculture, food & agri-tech",
        href: "/industries/agri-food",
        children: [
          { id: 1, label: "Agriculture & farming", href: "/industries/agriculture" },
          { id: 2, label: "Food & beverage", href: "/industries/food-beverage" },
          { id: 3, label: "AgriTech & supply traceability", href: "/industries/agritech" },
        ],
      },
      {
        id: 14,
        label: "Travel, hospitality & leisure",
        href: "/industries/travel-hospitality",
        children: [
          { id: 1, label: "Travel & tourism", href: "/industries/travel" },
          { id: 2, label: "Hospitality & hotels", href: "/industries/hospitality" },
          { id: 3, label: "Leisure & experiences", href: "/industries/leisure" },
        ],
      },
    ],
  },
  {
    id: 5,
    i18nKey: "root.hire",
    href: "/hire",
    children: [
      {
        id: 1,
        label: "Hire dedicated developers",
        href: "/hire/dedicated-developers",
        children: [
          {
            id: 1,
            label: "Web Developers",
            href: "/hire/dedicated-developers/web",
          },
          {
            id: 2,
            label: "Mobile Developers",
            href: "/hire/dedicated-developers/mobile",
          },
          {
            id: 3,
            label: "Full Stack Developers",
            href: "/hire/dedicated-developers/full-stack",
          },
          {
            id: 4,
            label: "Front-End Developers",
            href: "/hire/dedicated-developers/frontend",
          },
          {
            id: 5,
            label: "Back-End Developers",
            href: "/hire/dedicated-developers/backend",
          },
          {
            id: 6,
            label: "Cloud Engineers",
            href: "/hire/dedicated-developers/cloud",
          },
          {
            id: 7,
            label: "AI & ML Engineers",
            href: "/hire/dedicated-developers/ai-ml",
          },
          {
            id: 8,
            label: "Blockchain Developers",
            href: "/hire/dedicated-developers/blockchain",
          },
          {
            id: 9,
            label: "Game Developers",
            href: "/hire/dedicated-developers/game",
          },
        ],
      },
      {
        id: 2,
        label: "IT staff augmentation",
        href: "/hire/staff-augmentation",
        children: [
          {
            id: 1,
            label: "Short-Term Contracts",
            href: "/hire/staff-augmentation/short-term",
          },
          {
            id: 2,
            label: "Long-Term Contracts",
            href: "/hire/staff-augmentation/long-term",
          },
          {
            id: 3,
            label: "Onsite Augmentation",
            href: "/hire/staff-augmentation/onsite",
          },
          {
            id: 4,
            label: "Remote Augmentation",
            href: "/hire/staff-augmentation/remote",
          },
          {
            id: 5,
            label: "Specialized Skills",
            href: "/hire/staff-augmentation/specialized",
          },
        ],
      },
      {
        id: 3,
        label: "Dedicated teams",
        href: "/hire/dedicated-teams",
        children: [
          {
            id: 1,
            label: "Custom Software Teams",
            href: "/hire/dedicated-teams/software",
          },
          {
            id: 2,
            label: "Product Development Teams",
            href: "/hire/dedicated-teams/product",
          },
          {
            id: 3,
            label: "Maintenance & Support Teams",
            href: "/hire/dedicated-teams/support",
          },
          { id: 4, label: "Agile Squads", href: "/hire/dedicated-teams/agile" },
          {
            id: 5,
            label: "Extended Teams",
            href: "/hire/dedicated-teams/extended",
          },
        ],
      },
      {
        id: 4,
        label: "Offshore developers",
        href: "/hire/offshore-developers",
        children: [
          {
            id: 1,
            label: "Nearshore Developers",
            href: "/hire/offshore-developers/nearshore",
          },
          {
            id: 2,
            label: "Offshore Web Developers",
            href: "/hire/offshore-developers/web",
          },
          {
            id: 3,
            label: "Offshore Mobile Developers",
            href: "/hire/offshore-developers/mobile",
          },
          {
            id: 4,
            label: "Offshore Full Stack Teams",
            href: "/hire/offshore-developers/full-stack",
          },
          {
            id: 5,
            label: "Offshore Support & QA",
            href: "/hire/offshore-developers/support-qa",
          },
        ],
      },
      {
        id: 5,
        label: "Hire web developers",
        href: "/hire/web-developers",
        children: [
          { id: 1, label: "Angular", href: "/hire/web-developers/angular" },
          { id: 2, label: "React.js", href: "/hire/web-developers/react" },
          { id: 3, label: "Vue.js", href: "/hire/web-developers/vue" },
          {
            id: 4,
            label: "JavaScript",
            href: "/hire/web-developers/javascript",
          },
          { id: 5, label: "Svelte", href: "/hire/web-developers/svelte" },
          { id: 6, label: "Ruby on Rails", href: "/hire/web-developers/ruby" },
          {
            id: 7,
            label: "Full stack",
            href: "/hire/web-developers/fullstack",
          },
          {
            id: 8,
            label: "MEAN stack",
            href: "/hire/web-developers/mean-stack",
          },
          { id: 9, label: ".NET", href: "/hire/web-developers/dotnet" },
          { id: 10, label: "Java", href: "/hire/web-developers/java" },
          { id: 11, label: "PHP", href: "/hire/web-developers/php" },
          { id: 12, label: "Python", href: "/hire/web-developers/python" },
          { id: 13, label: "Golang", href: "/hire/web-developers/golang" },
          { id: 14, label: "COBOL", href: "/hire/web-developers/cobol" },
        ],
      },
      {
        id: 6,
        label: "Hire mobile developers",
        href: "/hire/mobile-developers",
        children: [
          { id: 1, label: "iOS", href: "/hire/mobile-developers/ios" },
          { id: 2, label: "Android", href: "/hire/mobile-developers/android" },
          { id: 3, label: "Kotlin", href: "/hire/mobile-developers/kotlin" },
          {
            id: 4,
            label: "React Native",
            href: "/hire/mobile-developers/react-native",
          },
          { id: 5, label: "Swift", href: "/hire/mobile-developers/swift" },
        ],
      },
      {
        id: 7,
        label: "Hire other engineers",
        href: "/hire/other-engineers",
        children: [
          { id: 1, label: "AI", href: "/hire/other-engineers/ai" },
          { id: 2, label: "Cloud", href: "/hire/other-engineers/cloud" },
          { id: 3, label: "AWS", href: "/hire/other-engineers/aws" },
          { id: 4, label: "Azure", href: "/hire/other-engineers/azure" },
          { id: 5, label: "DevOps", href: "/hire/other-engineers/devops" },
          { id: 6, label: "QA", href: "/hire/other-engineers/qa" },
          { id: 7, label: "Fintech", href: "/hire/other-engineers/fintech" },
          { id: 8, label: "SAP", href: "/hire/other-engineers/sap" },
          { id: 9, label: "Odoo", href: "/hire/other-engineers/odoo" },
          {
            id: 10,
            label: "Salesforce",
            href: "/hire/other-engineers/salesforce",
          },
          { id: 11, label: "Shopify", href: "/hire/other-engineers/shopify" },
          { id: 12, label: "UiPath", href: "/hire/other-engineers/uipath" },
          { id: 13, label: "Data engineers", href: "/hire/other-engineers/data-engineers" },
          { id: 14, label: "MLOps engineers", href: "/hire/other-engineers/mlops" },
        ],
      },
      {
        id: 8,
        label: "Hire data, AI & automation experts",
        href: "/hire/data-ai-automation",
        children: [
          { id: 1, label: "ML & AI engineers", href: "/hire/data-ai-automation/ml-ai" },
          { id: 2, label: "Data scientists & analysts", href: "/hire/data-ai-automation/data-science" },
          { id: 3, label: "Data engineers & platform", href: "/hire/data-ai-automation/data-engineering" },
          { id: 4, label: "LLM / LangChain engineers", href: "/hire/data-ai-automation/llm-engineers" },
          { id: 5, label: "MLOps & LLM ops", href: "/hire/data-ai-automation/mlops-llmops" },
          { id: 6, label: "Prompt & AI solution engineers", href: "/hire/data-ai-automation/prompt-engineers" },
          { id: 7, label: "RPA & intelligent automation", href: "/hire/data-ai-automation/rpa" },
          { id: 8, label: "AI agent developers", href: "/hire/data-ai-automation/agent-developers" },
        ],
      },
    ],
  },
  {
    id: 6,
    i18nKey: "root.portfolio",
    href: "/portfolio",
    children: [
      {
        id: 1,
        label: "Case studies",
        href: "/portfolio/case-studies",
        children: [
          { id: 1, label: "Banking", href: "/portfolio/case-studies/banking" },
          { id: 2, label: "Finance", href: "/portfolio/case-studies/finance" },
          {
            id: 3,
            label: "Healthcare",
            href: "/portfolio/case-studies/healthcare",
          },
          { id: 4, label: "Pharma", href: "/portfolio/case-studies/pharma" },
          {
            id: 5,
            label: "eCommerce",
            href: "/portfolio/case-studies/ecommerce",
          },
          { id: 6, label: "Retail", href: "/portfolio/case-studies/retail" },
          {
            id: 7,
            label: "Manufacturing",
            href: "/portfolio/case-studies/manufacturing",
          },
          {
            id: 8,
            label: "Enterprise",
            href: "/portfolio/case-studies/enterprise",
          },
          {
            id: 9,
            label: "Logistics & supply chain",
            href: "/portfolio/case-studies/logistics",
          },
          {
            id: 10,
            label: "Government & public sector",
            href: "/portfolio/case-studies/government",
          },
          { id: 11, label: "AI & machine learning", href: "/portfolio/case-studies/ai-ml" },
          { id: 12, label: "SaaS & B2B platforms", href: "/portfolio/case-studies/saas" },
          { id: 13, label: "Cybersecurity", href: "/portfolio/case-studies/cybersecurity" },
          { id: 14, label: "Telecommunications", href: "/portfolio/case-studies/telecom" },
        ],
      },
      {
        id: 2,
        label: "Our platforms",
        href: "/portfolio/platforms",
        children: [
          {
            id: 1,
            label: "Applicant tracking system (ATS)",
            href: "/portfolio/platforms/ats",
          },
          { id: 2, label: "HRM system", href: "/portfolio/platforms/hrm" },
          {
            id: 3,
            label: "Learning management system (LMS)",
            href: "/portfolio/platforms/lms",
          },
          {
            id: 4,
            label: "Workspace booking system",
            href: "/portfolio/platforms/workspace-booking",
          },
          {
            id: 5,
            label: "Customer data platform (CDP)",
            href: "/portfolio/platforms/cdp",
          },
          {
            id: 6,
            label: "AI agent & workflow orchestration",
            href: "/portfolio/platforms/ai-orchestration",
          },
        ],
      },
    ],
  },
  { id: 7, i18nKey: "root.about", href: "/about" },
  { id: 8, i18nKey: "root.contact", href: "/consultation" },
];
